import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchTopMovers } from "../lib/api";
import { CaretUp, CaretDown, Lightning } from "@phosphor-icons/react";
import { formatPct, formatSignedPct } from "../lib/format";

const FIELD_BY_WINDOW = {
  "1h": "oneHourPriceChange",
  "24h": "oneDayPriceChange",
  "1w": "oneWeekPriceChange",
};

export default function TopMoversStrip() {
  const [win, setWin] = useState("24h");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchTopMovers(win, 12)
      .then((d) => {
        if (cancelled) return;
        setItems(d.items || []);
      })
      .catch(() => {})
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [win]);

  const field = FIELD_BY_WINDOW[win];

  return (
    <section
      data-testid="top-movers-strip"
      className="border-b border-white/10 bg-zinc-950/40"
    >
      <div className="mx-auto max-w-[1600px] px-6 py-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Lightning size={14} weight="fill" className="text-edge" />
            <div className="text-[10px] uppercase tracking-[0.32em] font-mono text-edge">
              ◢ Top Movers
            </div>
          </div>
          <div className="flex border border-white/15" data-testid="top-movers-window-toggle">
            {["1h", "24h", "1w"].map((w) => (
              <button
                key={w}
                data-testid={`movers-win-${w}`}
                onClick={() => setWin(w)}
                className={`px-2.5 py-1 text-[10px] uppercase tracking-widest font-mono transition-colors ${
                  win === w ? "bg-white text-black" : "text-zinc-400 hover:text-white"
                }`}
              >
                {w}
              </button>
            ))}
          </div>
        </div>

        <div className="flex gap-3 overflow-x-auto pb-1 -mx-1 px-1 snap-x">
          {loading && items.length === 0 ? (
            Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="shrink-0 w-[260px] h-[78px] border border-white/10 animate-pulse-soft bg-white/[0.02]"
              />
            ))
          ) : items.length === 0 ? (
            <div className="text-zinc-500 font-mono text-xs py-4">No movers in window.</div>
          ) : (
            items.map((m) => {
              const change = Number(m[field] || 0);
              const up = change >= 0;
              return (
                <Link
                  key={m.id}
                  to={`/market/${m.id}`}
                  data-testid={`mover-card-${m.id}`}
                  className="snap-start shrink-0 w-[260px] border border-white/10 hover:border-white/30 bg-zinc-950/60 p-3 transition-colors group flex gap-2"
                >
                  {m.image && (
                    <img src={m.image} alt="" className="w-9 h-9 object-cover border border-white/10 shrink-0 grayscale group-hover:grayscale-0 transition-all" />
                  )}
                  <div className="flex-1 min-w-0 flex flex-col">
                    <div className="font-display text-xs font-medium line-clamp-2 text-zinc-100">
                      {m.question}
                    </div>
                    <div className="mt-auto flex items-center justify-between text-[11px] font-mono tabular-nums">
                      <span className="text-zinc-300">{formatPct(m.yesPrice, 0)}</span>
                      <span className={`inline-flex items-center gap-0.5 ${up ? "text-profit" : "text-loss"}`}>
                        {up ? <CaretUp size={10} weight="fill" /> : <CaretDown size={10} weight="fill" />}
                        {formatSignedPct(change, 1)}
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })
          )}
        </div>
      </div>
    </section>
  );
}
